import React, { RefObject, useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import styled from 'styled-components';
import { Maybe, TowingRecordPaymentType } from '../../../../../common/types';
import {
  FormSubGroup,
  FormSubGroupHeader,
  FormSubGroupHeading,
  Section,
  SectionColumn
} from '../../../../../components/layout';
import { DateInput, PhoneInput, SelectInput, TextInput } from '../../../../../components/layout/form/input';
import { messages as billingMessages } from '../BillingConfirmation/messages';
import { messages } from './messages';
import { PaymentTerminals } from '../../../../../components/PaymentTerminals';
import { EditTowingRecordFormValues } from '../../../EditTowingRecord/types';
import { formatMessageSafe } from '../../../../../util/translateInputOptions';
import { useIntl } from 'react-intl';
import { useSettings } from '../../../../Parameters';
import { Button } from '../../../../../components/Button';
import { ButtonColumn, ButtonRow } from '../../../../../components/layout';
import { Modal } from '../../../../../components/Modal';
import { setErrorNotifications, setFatalErrorNotification } from '../../../../../components/responses/setErrorNotifications';
import { setSuccessNotification } from '../../../../../components/notification';
import { useHistoryPush } from '../../../../../components/router';
import { getPrepaymentLabel } from '../../../../../components/TowingCommon/getPrepaymentLabel';
import { useFormikHelpers } from '../../../../../util/useFormikHelpers';
import { useInterval } from '../../../../../util/useInterval/useInterval';
import {
  isPaymentLinkAlreadyActive,
  isPaymentLinkSent,
  isAwaitingPaymentStatus,
  isTerminalBillingStatus,
  BillingStatus,
} from './types';
import {
  CANCEL_PAYMENT_LINK,
  PaymentLinkMutationVariables,
  RESEND_PAYMENT_LINK,
} from './mutations';

interface Props
  extends Readonly<{
    itemsTotal: Maybe<number>;
    values?: EditTowingRecordFormValues;
    hasFormChanged?: () => boolean
    paymentType: Maybe<TowingRecordPaymentType>;
    onChange: (debtorType: string) => void;
    disabled: boolean;
    billingRef?: RefObject<any>
    refetch?: () => Promise<any>
  }> {}

const isClosedPaymentType = (paymentType: TowingRecordPaymentType | null) =>
  paymentType &&
  [
    TowingRecordPaymentType.person,
    TowingRecordPaymentType.company,
    TowingRecordPaymentType.insurance_company,
    TowingRecordPaymentType.aggregate,
    TowingRecordPaymentType.define_later,
    TowingRecordPaymentType.settlement,

    TowingRecordPaymentType.autoliitto,
  ].includes(paymentType);

type PendingAction = 'resend' | 'cancel' | null

const PaymentStatusBadge = styled.span<{ variant: 'waiting' | 'completed' | 'failed' }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 1rem;
  font-size: 0.8rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  background-color: ${({ theme, variant }) =>
    variant === 'completed'
      ? theme.colors.green100
      : variant === 'failed'
        ? theme.colors.red50
        : theme.colors.orange100};
  color: ${({ theme, variant }) =>
    variant === 'completed'
      ? theme.colors.green
      : variant === 'failed'
        ? theme.colors.red
        : theme.colors.orange600};
`

const TowingRecordBillingIntl: React.FunctionComponent<Props> = ({
  itemsTotal,
  values,
  hasFormChanged,
  paymentType,
  onChange,
  disabled,
  billingRef,
  refetch,
}) => {
  const { setValue } = useFormikHelpers<EditTowingRecordFormValues>()
  const intl = useIntl()
  const historyPush = useHistoryPush()
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const open =
    !isClosedPaymentType(paymentType) &&
    (paymentType === null || paymentType === TowingRecordPaymentType.define_later ? false : true);
  const settings = useSettings();
  let paymentTerminalsEnabled: boolean = settings
    ? settings.paymentTerminalsEnabled
    : false;
  const paymentLinkEnabled = settings?.companyData?.paymentLinkEnabled ?? false
  const formatMessage = formatMessageSafe(intl)
  const paymentTypeOptions = [
    { value: null, label: formatMessage(messages.paymentTypeInProgress) },
    { value: TowingRecordPaymentType.define_later, label: formatMessage(messages.paymentTypeReadyForBilling) },
    {
      value: TowingRecordPaymentType.card,
      label: formatMessage(messages.paymentTypeCard),
    },
    { value: TowingRecordPaymentType.cash, label: formatMessage(messages.paymentTypeCash) },
    {
      value: TowingRecordPaymentType.online_payment,
      label: paymentLinkEnabled
        ? formatMessage(messages.paymentTypeOnlinePayment)
        : formatMessage(messages.paymentTypeOnlinePaymentDisabled),
      isDisabled: !paymentLinkEnabled,
    },
  ];

  const debtorOptions = (): Record<string, string>[] => {
    return [
      { value: 'orderer', label: formatMessage(messages.debtorOrderer) },
      { value: 'holder', label: formatMessage(messages.debtorHolder) },
      { value: 'owner', label: formatMessage(messages.debtorOwner) },
      { value: 'other', label: formatMessage(messages.debtorOther) },
    ];
  }

  const confirmationConfig = {
    resend: {
      label: formatMessage(messages.resendLabel),
      message: formatMessage(messages.resendMessage),
      confirmLabel: formatMessage(messages.resendLabel),
      confirmButtonCategory: 'save' as const,
      successTitle: formatMessage(messages.resendSuccessTitle),
      successMessage: formatMessage(messages.resendSuccessMessage),
    },
    cancel: {
      label: formatMessage(messages.cancelLinkLabel),
      message: formatMessage(messages.cancelLinkMessage),
      confirmLabel: formatMessage(messages.cancelLinkLabel),
      confirmButtonCategory: 'danger' as const,
      successTitle: formatMessage(messages.cancelLinkSuccessTitle),
      successMessage: formatMessage(messages.cancelLinkSuccessMessage),
    },
  }

  const towingRecordId = values?.id
  const billingStatus = values?.billing?.status
  const linkIsSent = isPaymentLinkSent(billingStatus)
  const isOnlinePayment = paymentType === TowingRecordPaymentType.online_payment
  const paymentLinkLocked = isOnlinePayment && isPaymentLinkAlreadyActive(paymentType, billingStatus)
  const billingFieldsDisabled = disabled || paymentLinkLocked

  const normalizedStatus = billingStatus === 'sent' ? 'link_sent' : billingStatus
  const paymentStatusLabel = isOnlinePayment
    ? getPrepaymentLabel(id => formatMessage({ id }), normalizedStatus, { prepayment: values?.prepayment })
    : null
  const badgeVariant =
    billingStatus === BillingStatus.open ||
    billingStatus === BillingStatus.paid ||
    billingStatus === BillingStatus.fully_paid
      ? 'completed'
      : billingStatus === BillingStatus.link_expired || isTerminalBillingStatus(billingStatus)
        ? 'failed'
        : 'waiting'

  const refreshBillingStatus = useCallback(async () => {
    if (!refetch || !isAwaitingPaymentStatus(billingStatus)) return
    try {
      const result = await refetch()
      const record = result.data?.towingRecord
      if (record?.__typename === 'GetTowingRecordSuccess') {
        const newStatus = record.towingRecord.billing?.[0]?.status
        if (newStatus !== undefined && newStatus !== billingStatus) {
          setValue('billing.status', newStatus)
        }
      }
    } catch {}
  }, [refetch, billingStatus, setValue])

  const shouldPollBillingStatus =
    isOnlinePayment && !!refetch && isAwaitingPaymentStatus(billingStatus)

  useInterval(refreshBillingStatus, shouldPollBillingStatus ? 5000 : null)

  const mutationOptions = (typename: string, action: 'resend' | 'cancel') => ({
    onCompleted(result: any) {
      const payload = result[`${action}PaymentLink`]
      if (payload.__typename === typename) {
        const cfg = confirmationConfig[action]
        setSuccessNotification(cfg.successTitle, cfg.successMessage)
        if (towingRecordId) historyPush(`/towing-record/${towingRecordId}`)
      } else {
        setErrorNotifications({ data: payload })
      }
    },
    onError(err: any) {
      setFatalErrorNotification(err)
    },
  })

  const [doResend, { loading: resendLoading }] = useMutation<any, PaymentLinkMutationVariables>(
    RESEND_PAYMENT_LINK,
    mutationOptions('ResendPaymentLinkSuccess', 'resend'),
  )
  const [doCancel, { loading: cancelLoading }] = useMutation<any, PaymentLinkMutationVariables>(
    CANCEL_PAYMENT_LINK,
    mutationOptions('CancelPaymentLinkSuccess', 'cancel'),
  )

  const mutationLoading = resendLoading || cancelLoading

  const handleConfirm = () => {
    if (!towingRecordId || !pendingAction) return
    const input = { towingRecordId }
    if (pendingAction === 'resend') doResend({ variables: { input } })
    else if (pendingAction === 'cancel') doCancel({ variables: { input } })
    setPendingAction(null)
  }

  return (
    <>
      {pendingAction && (
        <Modal
          label={confirmationConfig[pendingAction].label}
          message={confirmationConfig[pendingAction].message}
          close={() => setPendingAction(null)}
          onConfirm={handleConfirm}
          confirmLabel={confirmationConfig[pendingAction].confirmLabel}
          confirmButtonCategory={confirmationConfig[pendingAction].confirmButtonCategory}
          closeLabel={formatMessage(messages.cancel)}
        />
      )}
      <FormSubGroupHeader>
        <FormSubGroupHeading>{formatMessage(messages.header)}</FormSubGroupHeading>
        {isOnlinePayment && paymentStatusLabel && (
          <PaymentStatusBadge variant={badgeVariant}>{paymentStatusLabel}</PaymentStatusBadge>
        )}
      </FormSubGroupHeader>
      <FormSubGroup ref={billingRef}>
        <Section>
          <SectionColumn>
            <SelectInput
              label={formatMessage(messages.selectPaymentType)}
              name="paymentType"
              options={paymentTypeOptions}
              disabled={billingFieldsDisabled}
              searchable={false}
              placeholder={
                billingFieldsDisabled && paymentType
                  ? formatMessage(billingMessages[paymentType])
                  : formatMessage(messages.selectPlaceholder)
              }
            />
          </SectionColumn>
        </Section>
      </FormSubGroup>
      {open && (
        <FormSubGroup>
          <Section>
            <SectionColumn>
              <SelectInput
                label={formatMessage(messages.debtorTypeLabel)}
                name="debtorType"
                options={debtorOptions()}
                disabled={billingFieldsDisabled}
                onChange={(selection) => {
                  if (selection && typeof selection !== 'number') {
                    onChange(selection);
                  }
                }}
              />
            </SectionColumn>
            <SectionColumn>
              <TextInput
                label={formatMessage(messages.debtorNameLabel)}
                name="billing.paymentDetails.debtorName"
                disabled={billingFieldsDisabled}
              />
            </SectionColumn>
            {!isOnlinePayment && (
              <SectionColumn>
                <DateInput
                  label={formatMessage(messages.paymentDateLabel)}
                  name="billing.paymentDetails.paymentDate"
                  disabled={billingFieldsDisabled}
                />
              </SectionColumn>
            )}
            <SectionColumn>
              <TextInput
                label={formatMessage(messages.receiptEmailLabel)}
                name="billing.paymentDetails.receiptEmail"
                disabled={billingFieldsDisabled}
                type="email"
              />
            </SectionColumn>
            {isOnlinePayment && (
              <SectionColumn>
                <PhoneInput
                  label={formatMessage({
                    id: 'towingRecord.billing.paymentDetails.debtorPhone',
                    defaultMessage: 'Puhelinnumero',
                  })}
                  name="billing.paymentDetails.debtorPhone"
                  disabled={billingFieldsDisabled}
                  required
                />
              </SectionColumn>
            )}
          </Section>
        </FormSubGroup>
      )}
      {isOnlinePayment && linkIsSent && (
      <FormSubGroup>
        <Section>
          <SectionColumn>
            <ButtonRow padding="0" justify="start">
              <ButtonColumn>
                <Button
                  category="save"
                  label={formatMessage(messages.resendLabel)}
                  onClick={() => setPendingAction('resend')}
                  disabled={mutationLoading}
                />
              </ButtonColumn>
              <ButtonColumn>
                <Button
                  category="danger"
                  label={formatMessage(messages.cancelLinkLabel)}
                  onClick={() => setPendingAction('cancel')}
                  disabled={mutationLoading}
                />
              </ButtonColumn>
            </ButtonRow>
          </SectionColumn>
        </Section>
      </FormSubGroup>
      )}
      {paymentType === 'card' && paymentTerminalsEnabled && (
        <PaymentTerminals itemsTotal={itemsTotal} values={values} hasFormChanged={hasFormChanged} />
      )}
    </>
  );
};

export const TowingRecordBilling = TowingRecordBillingIntl;
